import React, { useState } from 'react';

// Single contact form with a category dropdown (replaces the old General /
// Technical tabs). The selected category drives both the textarea placeholder
// and the email subject (the backend maps `type` -> subject). Validation is
// inline; a Cancel button closes the popover (Esc / click-outside are handled
// by Navbar, which owns the toggle + popover wrapper).
const CATEGORIES = [
  { value: 'General', label: 'General Inquiry', placeholder: 'How can we help?' },
  { value: 'Feedback', label: 'Leave Feedback', placeholder: 'Share your feedback...' },
  { value: 'Technical', label: 'Technical Issue', placeholder: 'Describe the issue...' },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ContactForm = ({ onClose }) => {
  const [category, setCategory] = useState('General');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', message: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const activeCategory = CATEGORIES.find((c) => c.value === category) ?? CATEGORIES[0];

  const handleSubmit = async () => {
    if (submitting) return;

    const email = form.email.trim();
    const message = form.message.trim();

    if (!message) {
      setError('Please enter a message before submitting.');
      return;
    }
    if (!EMAIL_REGEX.test(email) || email.length > 254) {
      setError('Please provide a valid email address.');
      return;
    }

    setError('');
    setSubmitting(true);

    try {
      const response = await fetch('/api/contact-us', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: category,
          firstName: form.firstName.trim(),
          lastName: form.lastName.trim(),
          userEmail: email,
          requestContent: message,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSent(true);
      } else {
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      console.error('Submission Error:', err);
      setError('Network error. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  // Anchored flush against the toggle button: Navbar keeps the button's own
  // wrapping div margin-free, so `right: 0` here lands exactly at the
  // button's right edge and `top: '100%'` starts right at its bottom edge,
  // at every breakpoint, without any hardcoded pixel offsets to keep in sync.
  const containerStyle = {
    position: 'absolute',
    top: '100%',
    right: 0,
    width: '450px',
    backgroundColor: '#4a7094',
    padding: '20px',
    borderRadius: '15px 0 15px 15px',
    zIndex: 10,
    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
    color: 'white',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
  };

  const columnStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    padding: '25px',
    border: '3px solid rgba(255,255,255,0.2)',
    borderRadius: '10px',
    boxSizing: 'border-box',
  };

  const inputStyle = {
    width: '100%',
    padding: '8px',
    borderRadius: '4px',
    border: 'none',
    textAlign: 'center',
    boxSizing: 'border-box',
  };

  const selectStyle = {
    width: '100%',
    padding: '10px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#D1DEE6',
    color: '#2B4B6F',
    fontWeight: 'bold',
    fontSize: '16px',
    textAlign: 'center',
    textAlignLast: 'center',
    cursor: 'pointer',
    boxSizing: 'border-box',
    marginBottom: '5px',
  };

  const submitStyle = {
    backgroundColor: '#31a0ff',
    color: 'white',
    border: 'none',
    padding: '10px',
    borderRadius: '10px',
    cursor: submitting ? 'default' : 'pointer',
    fontWeight: 'bold',
    width: '100%',
    boxSizing: 'border-box',
    opacity: submitting ? 0.7 : 1,
  };

  const cancelStyle = {
    backgroundColor: 'transparent',
    color: 'white',
    border: '1px solid rgba(255,255,255,0.6)',
    padding: '10px',
    borderRadius: '10px',
    cursor: 'pointer',
    fontWeight: 'bold',
    width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <>
      <style>
        {`
          @media (max-width: 1100px) {
            .contact-container-responsive {
              width: 90vw !important;
              max-width: 400px;
            }
          }
          @media (max-width: 800px) {
            .contact-container-responsive {
              width: 75vw !important;
              max-width: 340px !important;
              padding: 13px !important;
              font-size: 11px !important;
              border-radius: 15px !important;
            }
            .contact-announcement {
              padding: 8px !important;
              font-size: 11px !important;
              margin-bottom: 10px !important;
            }
            .contact-column { padding: 15px !important; gap: 8px !important; }
            .contact-column h3 { font-size: 12px !important; }
            .contact-input { padding: 6px !important; font-size: 12px !important; }
            .contact-textarea { height: 100px !important; }
            .contact-submit { padding: 8px !important; font-size: 14px !important; }
          }
          @media (max-width: 600px) {
            .contact-container-responsive {
              width: 75vw !important;
              max-width: 340px !important;
              padding: 13px !important;
              font-size: 11px !important;
            }
            .contact-announcement {
              padding: 8px !important;
              font-size: 11px !important;
              margin-bottom: 10px !important;
            }
            .contact-column { padding: 15px !important; gap: 8px !important; }
            .contact-column h3 { font-size: 12px !important; }
            .contact-input { padding: 6px !important; font-size: 12px !important; }
            .contact-textarea { height: 100px !important; }
            .contact-submit { padding: 8px !important; font-size: 14px !important; }
          }
        `}
      </style>

      <div className="contact-container-responsive" style={containerStyle}>
        {/* Top Announcement Bar */}
        <div
          className="contact-announcement"
          style={{ backgroundColor: '#B3CFDB', color: 'black', padding: '15px', marginBottom: '15px', textAlign: 'center', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' }}
        >
          Contact us and our team will respond back in less than 72 hours!
        </div>

        <div className="contact-column" style={columnStyle}>
          {sent ? (
            <>
              <h3 style={{ fontSize: '16px', textAlign: 'center', margin: '0 0 10px 0' }}>
                ✓ Message sent!
              </h3>
              <p style={{ textAlign: 'center', margin: 0, fontSize: '14px' }}>
                Thanks for reaching out — our team will respond within 72 hours.
              </p>
              <button type="button" className="contact-submit" onClick={onClose} style={submitStyle}>
                Close
              </button>
            </>
          ) : (
            <>
              {/* Category dropdown (replaces the old tabs + section header) */}
              <label htmlFor="contact-category" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
                Inquiry category
              </label>
              <select
                id="contact-category"
                className="contact-input"
                style={selectStyle}
                value={category}
                onChange={(e) => { setCategory(e.target.value); setError(''); }}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>

              <div style={{ display: 'flex', gap: '5px' }}>
                <input
                  className="contact-input"
                  style={inputStyle}
                  aria-label="First name"
                  placeholder="First Name"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
                <input
                  className="contact-input"
                  style={inputStyle}
                  aria-label="Last name"
                  placeholder="Last Name"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </div>

              <input
                className="contact-input"
                style={inputStyle}
                aria-label="Email address"
                placeholder="Email Address"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />

              <textarea
                className="contact-input contact-textarea"
                aria-label="Message"
                placeholder={activeCategory.placeholder}
                style={{ ...inputStyle, height: '120px', textAlign: 'left' }}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />

              {error && (
                <p style={{ color: '#ffd2d2', backgroundColor: 'rgba(176,0,32,0.25)', padding: '8px', borderRadius: '6px', fontSize: '13px', margin: 0, textAlign: 'center' }}>
                  {error}
                </p>
              )}

              <button type="button" className="contact-submit" onClick={handleSubmit} disabled={submitting} style={submitStyle}>
                {submitting ? 'Sending...' : 'Submit Form'}
              </button>
              <button type="button" onClick={onClose} style={cancelStyle}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ContactForm;
